// Ekran 05 — "Şablondan ünite ekle" 2 adımlı drawer (K25).
//
// Adım 1: 16 sınıflık kütüphaneden seçim (.lib-i radio kartları, gruplara ayrık)
// Adım 2: kod/ad + teknik değerler + beyan sıklıkları
//
// Adım geçişi TEK drawer içinde: referans da aynı katmanı yeniden dolduruyor,
// 2. seviye açmıyor.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'

import {
  FREK_ETIKET,
  GRUP_ETIKET,
  SIKLIKLAR,
  uniteler as uniteApi,
  type UniteGrubu,
  type UniteSinifiDto,
} from './api'

const GRUP_SIRASI: UniteGrubu[] = ['on', 'cur', 'depo', 'gaz', 'enerji']

export default function SablonDrawer() {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const [adim, setAdim] = useState<1 | 2>(1)
  const [sinif, setSinif] = useState<UniteSinifiDto | null>(null)
  const [ad, setAd] = useState('')
  /** Ad kullanıcı tarafından mı yazıldı — evetse otomatik öneri üzerine yazmaz. */
  const [adElle, setAdElle] = useState(false)
  const [teknik, setTeknik] = useState<Record<string, string>>({})
  const [frekler, setFrekler] = useState<Record<string, string>>({})

  const { data: siniflar = [] } = useQuery({
    queryKey: ['unite-siniflari'],
    queryFn: uniteApi.siniflar,
  })

  const { data: sonrakiKod } = useQuery({
    queryKey: ['unite-sonraki-kod', sinif?.id],
    queryFn: () => uniteApi.sonrakiKod(sinif!.id),
    enabled: !!sinif,
  })

  const gruplar = useMemo(
    () => GRUP_SIRASI.map((g) => ({ grup: g, uyeler: siniflar.filter((s) => s.grup === g) }))
      .filter((x) => x.uyeler.length > 0),
    [siniflar],
  )

  function sinifSec(s: UniteSinifiDto) {
    setSinif(s)
    // Kod (dolayısıyla sıra no) sunucudan asenkron gelir; o gelene kadar
    // numarasız ad duruyor, aşağıdaki etki numarayı ekliyor.
    setAd(s.ad)
    setAdElle(false)
    setTeknik({})
    // Sıklıklar şablon varsayılanıyla başlar; kullanıcı değiştirebilir.
    setFrekler(Object.fromEntries(s.parametreler.map((p) => [p.ad, p.frek])))
  }

  /**
   * Referans :654-658 — önerilen ad sınıfın kaçıncı ünitesi olduğunu taşır
   * ("Çürütücü 3"); sınıfın ilk ünitesinde numara yazılmaz.
   *
   * Sıra no KOD'un sonekinden çözülüyor: referans n'i "mevcut ünite sayısı+1"
   * sayarken sunucu `sonraki-kod` ucu "en büyük sonek+1" veriyor. Silme sonrası
   * ikisi ayrışabilir; ad ile kodun tutarlı kalması için sunucu esas alındı.
   */
  useEffect(() => {
    if (!sinif || !sonrakiKod || adElle) return
    const n = Number(sonrakiKod.kod.slice(sinif.kodPrefix.length + 1))
    setAd(Number.isFinite(n) && n > 1 ? `${sinif.ad} ${n}` : sinif.ad)
  }, [sinif, sonrakiKod, adElle])

  const olustur = useMutation({
    mutationFn: async () => {
      const unite = await uniteApi.olustur({
        kod: sonrakiKod!.kod,
        ad: ad.trim(),
        uniteSinifiId: sinif!.id,
        durum: 'aktif',
        // Ham metin gider: teknik alanların bir kısmı serbest metin ("Rejim",
        // "Sensör seti") — sayıya çevrilirse sunucuda düşerdi.
        teknikDegerler: Object.fromEntries(
          Object.entries(teknik).filter(([, v]) => v.trim() !== ''),
        ),
      })

      // Sıklıklar şablon varsayılanından farklıysa parametreleri güncelle.
      const detay = await uniteApi.detay(unite.id)
      const degisen = detay.parametreler.some((p) => frekler[p.ad] && frekler[p.ad] !== p.frek)
      if (degisen) {
        await uniteApi.parametreler(
          unite.id,
          detay.parametreler.map((p) => ({
            id: p.id, ad: p.ad, birim: p.birim,
            frek: frekler[p.ad] ?? p.frek, kaynak: p.kaynak, sira: p.sira,
          })),
        )
      }
      return unite
    },
    onSuccess: (u) => {
      kuyruk.invalidateQueries({ queryKey: ['uniteler'] })
      kuyruk.invalidateQueries({ queryKey: ['unite-ozeti'] })
      const saatlik = Object.values(frekler).filter((f) => f === '1').length
      toast(`${u.kod} · ${u.ad} oluşturuldu — beyan gridi ${saatlik} saatlik alanla güncellendi`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Ünite oluşturulamadı'),
  })

  // ------------------------------------------------------------- 1. adım

  if (adim === 1) {
    return (
      <>
        <DrawerGovde>
          {gruplar.map(({ grup, uyeler }) => (
            <div key={grup}>
              <div className="insp-sec-t" style={{ margin: '8px 2px 5px' }}>
                {GRUP_ETIKET[grup]}
              </div>
              {uyeler.map((s) => (
                <label className="lib-i" key={s.id}>
                  <input
                    type="radio"
                    name="sn"
                    checked={sinif?.id === s.id}
                    onChange={() => sinifSec(s)}
                  />
                  <div>
                    <div className="lib-n">{s.ad}</div>
                    <div className="lib-m">{s.aciklama ?? '—'}</div>
                  </div>
                  {/* Referans :645 — parametre sayısı label'ın SON çocuğu olan
                      ayrı bir rozet; sağa yaslamayı .lib-v'nin margin-left:auto'su
                      yapar, satıriçi stil yazılmaz. */}
                  <span className="lib-v">{s.parametreler.length} beyan par.</span>
                </label>
              ))}
            </div>
          ))}
        </DrawerGovde>

        <DrawerAltlik>
          <button className="btn btn-sec" type="button" onClick={() => kapat()}>
            Vazgeç
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!sinif}
            title={sinif ? undefined : 'Önce bir sınıf seçin'}
            onClick={() => setAdim(2)}
          >
            Devam →
          </button>
        </DrawerAltlik>
      </>
    )
  }

  // ------------------------------------------------------------- 2. adım

  return (
    <>
      <DrawerGovde>
        <div className="fgrid" style={{ marginBottom: 10 }}>
          <div className="fld">
            <label>Ünite kodu</label>
            <input className="in mono" readOnly value={sonrakiKod?.kod ?? '…'} />
          </div>
          <div className="fld">
            <label>Ünite adı</label>
            <input
              className="in"
              value={ad}
              onChange={(e) => { setAd(e.target.value); setAdElle(true) }}
            />
          </div>

          {sinif!.teknikAlanlar.map((t) => (
            <div className="fld" key={t.anahtar}>
              <label>
                {t.etiket}
                {t.birim && t.birim !== '-' ? ` (${t.birim})` : ''}
              </label>
              <input
                className="in mono"
                placeholder="—"
                value={teknik[t.anahtar] ?? ''}
                onChange={(e) => setTeknik((d) => ({ ...d, [t.anahtar]: e.target.value }))}
              />
            </div>
          ))}
        </div>

        <div className="insp-sec-t" style={{ marginBottom: 5 }}>
          Beyan parametreleri — şablon varsayılanı, değiştirilebilir
        </div>
        {sinif!.parametreler.map((p) => (
          <div className="bp-row" key={p.ad}>
            <span>{p.ad}</span>
            <span className="bpu">{p.birim ?? '—'}</span>
            <FrekSecici
              deger={frekler[p.ad] ?? p.frek}
              kaynak={p.kaynak}
              degisti={(v) => setFrekler((d) => ({ ...d, [p.ad]: v }))}
            />
          </div>
        ))}

        <div className="info-box" style={{ marginTop: 10 }}>
          <div>
            Kaydedince ünite tesis yapılanmasına eklenir ve <b>Üretim Beyanı</b> gridi
            bu sıklıklara göre yeniden kurulur (K23).
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => setAdim(1)}>
          ← Geri
        </button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={ad.trim() === '' || olustur.isPending}
          onClick={() => olustur.mutate()}
        >
          {olustur.isPending ? 'Oluşturuluyor…' : 'Üniteyi oluştur'}
        </button>
      </DrawerAltlik>
    </>
  )
}

/**
 * K23 sıklık seçicisi. K21: lab kaynaklı parametre laboratuvardan dönemsiz
 * beslenir — seçici KİLİTLİ ve tek seçenek gösterir.
 */
export function FrekSecici({
  deger,
  kaynak,
  degisti,
}: {
  deger: string
  kaynak: 'manuel' | 'lab' | 'olay'
  degisti: (v: string) => void
}) {
  if (kaynak === 'lab') {
    return (
      <select className="frek-sel" disabled title="K21: laboratuvardan dönemsiz beslenir">
        <option>{FREK_ETIKET.lab}</option>
      </select>
    )
  }

  return (
    <select className="frek-sel" value={deger} onChange={(e) => degisti(e.target.value)}>
      {SIKLIKLAR.map((f) => (
        <option key={f} value={f}>
          {FREK_ETIKET[f]}
        </option>
      ))}
      <option value="olay">{FREK_ETIKET.olay}</option>
    </select>
  )
}
